import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

import ContactModal from "../../../../Assets/Common/ContactModal";

const CTASection = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleGetStarted = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <>
      <section
        className="
          relative
          overflow-hidden
          bg-[var(--bg-primary)]
          py-2
          text-[var(--text-primary)]
          transition-colors
          duration-500
          sm:py-4
          md:py-6
          lg:py-10
        "
      >
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-72
            w-72
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[var(--brand-primary)]/[0.06]
            blur-[120px]
            sm:h-96
            sm:w-96
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4
            sm:px-6
            lg:px-8
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.6,
              ease: "easeOut",
            }}
            className="
              relative
              overflow-hidden
              rounded-2xl
              border
              border-[var(--border-primary)]
              bg-[var(--card-bg)]
              px-5
              py-8
              shadow-[0_20px_60px_var(--shadow-color)]
              transition-colors
              duration-500
              sm:rounded-3xl
              sm:px-8
              sm:py-10
              md:px-12
              md:py-12
              lg:px-14
              lg:py-14
            "
          >
            <div
              className="
                pointer-events-none
                absolute
                -right-20
                -top-20
                h-48
                w-48
                rounded-full
                bg-[var(--brand-primary)]/[0.06]
                blur-[80px]
                sm:h-64
                sm:w-64
              "
            />

            <div
              className="
                relative
                flex
                flex-col
                items-center
                justify-between
                gap-8
                text-center
                lg:flex-row
                lg:gap-12
                lg:text-left
              "
            >
              <div className="max-w-2xl">
                <span
                  className="
                    inline-flex
                    items-center
                    rounded-full
                    border
                    border-orange-500/20
                    bg-orange-500/[0.08]
                    px-3
                    py-1.5
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[2px]
                    text-orange-400
                    sm:px-4
                    sm:py-2
                    sm:text-xs
                    sm:tracking-[2.5px]
                  "
                >
                  Let's get started
                </span>

                <h2
                  className="
                    mt-4
                    text-2xl
                    font-bold
                    leading-tight
                    tracking-tight
                    text-[var(--text-primary)]
                    transition-colors
                    duration-500
                    sm:mt-5
                    sm:text-3xl
                    md:text-4xl
                    lg:text-5xl
                  "
                >
                  Ready to Simplify Your{" "}
                  <span className="text-[var(--brand-primary)]">
                    Legal Requirements?
                  </span>
                </h2>

                <p
                  className="
                    mt-3
                    max-w-xl
                    text-xs
                    leading-6
                    text-[var(--text-muted)]
                    transition-colors
                    duration-500
                    sm:mt-4
                    sm:text-sm
                    sm:leading-7
                    md:text-base
                  "
                >
                  Get expert guidance for your business registration,
                  compliance, licensing, and other legal requirements.
                </p>

                <div
                  className="
                    mt-6
                    flex
                    flex-col
                    gap-3
                    sm:mt-7
                    sm:flex-row
                    sm:justify-center
                    lg:justify-start
                  "
                >
                  <button
                    type="button"
                    onClick={handleGetStarted}
                    className="
                      group
                      inline-flex
                      items-center
                      justify-center
                      gap-2
                      rounded-xl
                      bg-[var(--brand-primary)]
                      px-5
                      py-3
                      text-sm
                      font-semibold
                      text-white
                      transition-all
                      duration-300
                      hover:-translate-y-0.5
                      hover:bg-[var(--brand-hover)]
                      active:translate-y-0
                      sm:px-6
                      sm:py-3.5
                    "
                  >
                    <span>Get Started</span>

                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/10
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                        sm:h-6
                        sm:w-6
                      "
                    >
                      <ArrowRight
                        size={14}
                        strokeWidth={2.2}
                        className="sm:h-[15px] sm:w-[15px]"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <div
                className="
                  grid
                  w-full
                  max-w-sm
                  grid-cols-2
                  gap-3
                  sm:gap-4
                  lg:max-w-xs
                "
              >
                {[
                  {
                    number: "50+",
                    label: "Businesses",
                  },
                  {
                    number: "100%",
                    label: "Compliance",
                  },
                  {
                    number: "30+",
                    label: "Solutions",
                  },
                  {
                    number: "24/7",
                    label: "Support",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{
                      opacity: 0,
                      y: 15,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.4,
                      delay: 0.15 + index * 0.06,
                    }}
                    whileHover={{
                      y: -3,
                    }}
                    className="
                      rounded-xl
                      border
                      border-[var(--border-primary)]
                      bg-[var(--card-bg-secondary)]
                      px-3
                      py-4
                      text-center
                      shadow-[0_8px_24px_var(--shadow-color)]
                      transition-all
                      duration-300
                      hover:border-[var(--brand-primary)]/30
                      sm:rounded-2xl
                      sm:px-4
                      sm:py-5
                    "
                  >
                    <h3
                      className="
                        text-xl
                        font-bold
                        text-[var(--brand-primary)]
                        sm:text-2xl
                      "
                    >
                      {item.number}
                    </h3>

                    <p
                      className="
                        mt-1
                        text-[10px]
                        text-[var(--text-muted)]
                        transition-colors
                        duration-500
                        sm:text-xs
                      "
                    >
                      {item.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {isContactModalOpen && (
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={handleCloseContactModal}
            initialService="Legal Services"
            initialSubService=""
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CTASection;